import { FC, useCallback, useEffect, useState } from 'react';

import { useSetRecoilState } from 'recoil';

import { ChevronRightIcon, ClipboardListIcon } from '@heroicons/react/outline';
import {
  FeventOrder,
  FeventOrderHistory,
} from '@shared/components/GiftFeventOrderHistory';
import { GiftPhoneMemoPad } from '@shared/components/GiftPhoneMemoPad';
import { useAuth } from '@shared/state/hooks/useAuth';
import { getGiftAgentOrder } from '@shared/utils/giftAgentOrder.util';
import { pickRegion } from '@shared/utils/giftAddressRegion';
import { useLocation } from 'react-router-dom';

import { giftInputApplyRequestAtom } from '../state/giftInputApply.atom';
import { GiftAgentOrderResetButton } from './GiftAgentOrderResetButton';

/**
 * 대리주문(전화주문) 진행 중에만 뜨는 좌측 주문 이력 패널.
 *
 * 고객이 전화 전에 이미 결제를 시도했다가 전화한 경우가 많아, 그 상가(고인)의
 * 기존 주문·시도를 보면서 같은 상품·수량·감사글·배송지로 대신 주문할 수 있게 한다.
 * 좁은 화면(lg 미만)에서는 자리를 차지하지 않도록 하단 고정 버튼 → 전체 오버레이.
 *
 * 감사글·배송지는 **직원이 눌러야만** 주문서에 들어간다 (자동 채움 없음).
 * 이 상가엔 다른 가족의 주문도 함께 보이므로, 지금 응대 중인 상주와 주문자가
 * 다르면 한 번 확인을 받는다.
 */
export const GiftAgentOrderHistoryPanel: FC = function GiftAgentOrderHistoryPanel() {
  const location = useLocation();
  const { isAdmin, isGiftAgent } = useAuth();
  const [ctx, setCtx] = useState(() => getGiftAgentOrder());
  const [isOpen, setIsOpen] = useState(false);
  const setApplyRequest = useSetRecoilState(giftInputApplyRequestAtom);

  //* 결제 단계에서 상주 게스트로 전환되면(useGiftCheckoutPayment) 이 세션 토큰으로는
  //* 상담용 이력 조회(/gift-agent/gift-order/history)가 401이다. 그대로 두면 axios
  //* 인터셉터가 "권한 없음" 안내를 띄우므로, 조회 자체를 하지 않는다.
  //* (관리자 세션은 보관돼 있고 배너의 "종료"로 복원된다)
  const canViewHistory = isAdmin() || isGiftAgent();

  // 결제창 왕복·페이지 이동 후에도 컨텍스트 유무를 다시 확인
  useEffect(() => {
    setCtx(getGiftAgentOrder());
  }, [location]);

  //* 다른 가족(주문자 이름이 다른)의 주문 내용을 담을 때만 확인 — 명의 혼동 방지
  const confirmApply = useCallback(
    (order: FeventOrder, what: string): boolean => {
      if (!ctx) return false;
      const orderer = order.receiverName?.trim();
      if (!orderer || orderer === ctx.memberName) return true;
      return window.confirm(
        `${orderer}님이 넣은 주문의 ${what}입니다.\n${ctx.memberName}님 주문서에 그대로 채울까요?`,
      );
    },
    [ctx],
  );

  //* 반환값 = 실제로 채웠는지 (모바일 오버레이는 채웠을 때만 닫는다)
  const handleApplyThanks = useCallback(
    (order: FeventOrder): boolean => {
      if (!ctx || !order.thanksPrint?.message) return false;
      if (!confirmApply(order, '감사글')) return false;
      setApplyRequest({
        bugoId: ctx.bugoId,
        requestedAt: Date.now(),
        thanksPrint: {
          message: order.thanksPrint.message,
          senderName: order.thanksPrint.senderName,
          templateId: order.thanksPrint.templateId,
        },
      });
      return true;
    },
    [ctx, confirmApply, setApplyRequest],
  );

  const handleApplyAddress = useCallback(
    (order: FeventOrder): boolean => {
      const addr = order.bulkDeliveryAddress;
      if (!ctx || !addr?.address || !addr.postalCode) return false;
      if (!confirmApply(order, '배송지')) return false;
      setApplyRequest({
        bugoId: ctx.bugoId,
        requestedAt: Date.now(),
        deliveryAddress: {
          address: addr.address,
          postalCode: addr.postalCode,
          addressDetail: addr.addressDetail,
          region: pickRegion(addr),
        },
      });
      return true;
    },
    [ctx, confirmApply, setApplyRequest],
  );

  if (!ctx) return null;

  // 상가 정보가 없는 구버전 진입이거나 게스트로 전환된 뒤라면 메모장만
  const feventId = ctx.feventId;
  if (!feventId || !canViewHistory) return <GiftPhoneMemoPad raised />;

  return (
    <>
      {/* 데스크톱: "전화주문 진행 중" 배너 + 주문서 정리 버튼 아래 우측 고정 패널
            (스토어 본문은 가운데 정렬이라 겹치지 않는다) */}
      <aside className="fixed right-2 top-44 bottom-4 z-30 hidden w-96 flex-col overflow-y-auto rounded-lg border border-gray-200 bg-gray-100/95 p-3 shadow-lg lg:flex">
        <p className="mb-2 flex items-center gap-1 text-sm font-semibold text-gray-800">
          <ClipboardListIcon className="h-4 w-4" />
          {ctx.memberName}님 상가의 주문 내역
        </p>
        <FeventOrderHistory
          feventId={feventId}
          memberName={ctx.memberName}
          memberPhone={ctx.memberPhone}
          onApplyThanks={handleApplyThanks}
          onApplyAddress={handleApplyAddress}
        />
      </aside>

      {/* 모바일·태블릿: 하단 버튼 + 전체 오버레이 (메모는 좌측이라 우측에 둔다) */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-4 z-30 flex items-center gap-1 rounded-full bg-gray-800 px-4 py-2 text-xs font-medium text-white shadow-lg lg:hidden"
      >
        <ClipboardListIcon className="h-4 w-4" />
        주문 내역
      </button>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-white lg:hidden">
          <div className="flex items-center justify-between border-b border-gray-200 p-3">
            <p className="text-sm font-semibold text-gray-800">
              {ctx.memberName}님 상가의 주문 내역
            </p>
            <button
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-600"
            >
              닫기
              <ChevronRightIcon className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto bg-gray-50 p-3">
            <GiftAgentOrderResetButton
              ctx={ctx}
              onReset={() => setIsOpen(false)}
              className="mb-2 w-full"
            />
            {/* 채웠으면 주문서를 봐야 하므로 닫아준다 */}
            <FeventOrderHistory
              feventId={feventId}
              memberName={ctx.memberName}
              memberPhone={ctx.memberPhone}
              onApplyThanks={(order) => {
                if (handleApplyThanks(order)) setIsOpen(false);
              }}
              onApplyAddress={(order) => {
                if (handleApplyAddress(order)) setIsOpen(false);
              }}
            />
          </div>
        </div>
      )}

      {/* 통화 내용 메모 — 전화주문 관리 화면과 같은 내용이 이어진다 */}
      <GiftPhoneMemoPad raised />
    </>
  );
};
